import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthService } from 'providers/auth.service';
import {
    BehaviorSubject,
    Observable,
    Subject,
    catchError,
    combineLatest,
    delay,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    from,
    map,
    merge,
    of,
    shareReplay,
    startWith,
    switchMap,
} from 'rxjs';
import {
    SearchApiService,
    OrganizationSearchResponse,
    OrganizationSearchResultMinimized,
} from '@kempoweroyj/ngx-search-api-client';

const organizations$ = new BehaviorSubject<OrganizationSearchResultMinimized[]>(null);

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
/**
 * This Service is used to store organizations as a cache
 */
export class OrganizationService {
    private readonly refreshRequested$ = new Subject<number>();
    private _organizations$: Observable<OrganizationSearchResultMinimized[]>;

    get organizations(): OrganizationSearchResultMinimized[] {
        return organizations$.value;
    }
    get organizations$(): Observable<OrganizationSearchResultMinimized[]> {
        if (!this._organizations$) {
            const refresh$ = merge(
                this.refreshRequested$.pipe(
                    switchMap((delayTime) => (delayTime ? of(null).pipe(delay(delayTime)) : of(null)))
                )
            );
            const permissions$ = combineLatest([
                this.auth.authenticated$.pipe(
                    filter((isAuthenticated) => isAuthenticated),
                    distinctUntilChanged()
                ),
                this.auth.hasAccessTo$('sysadmin').pipe(distinctUntilChanged()),
                this.auth.hasAccessTo$('oemReadonly').pipe(distinctUntilChanged()),
            ]);
            const isInternalUsers$ = permissions$.pipe(
                map(([isAuthenticated, isSysAdmin, isOemReadonly]) => isAuthenticated && (isSysAdmin || isOemReadonly))
            );

            this._organizations$ = refresh$.pipe(
                startWith(null),
                switchMap(() => isInternalUsers$),
                switchMap((isInternalUser) => {
                    if (!isInternalUser) {
                        return of(null);
                    }
                    return this.requestAdminSearchOrganizations$({ minimized: true });
                }),
                map((res: OrganizationSearchResponse) => res?.results ?? []), // Return results or an empty array
                shareReplay(1) // Cache the response and share with future subscribers
            );
        }
        return this._organizations$;
    }

    constructor(
        private readonly auth: AuthService,
        private readonly searchApi: SearchApiService
    ) {
        this.auth.authenticated$.subscribe({
            next: (isAuthenticated) => {
                if (isAuthenticated) {
                    // Refresh organizations when user logs in or impersonates
                    this.refreshRequested$.next(0);
                }
            },
            complete: () => {
                // Clear organizations when user logs out
                organizations$.next(null);
            },
        });
    }

    getOrganizations(): Promise<OrganizationSearchResultMinimized[]> {
        return firstValueFrom(this.organizations$);
    }
    getOrganization(tenantId: string): Promise<OrganizationSearchResultMinimized> {
        return firstValueFrom(
            this.organizations$.pipe(
                map((organizations) => organizations.find((org) => org.organization.tenantId === tenantId))
            )
        );
    }

    // Refresh organizations
    refresh(withDelayMs: number = 0) {
        this.refreshRequested$.next(withDelayMs);
    }
    requestAdminSearchOrganizations$(opts: { minimized: boolean } = { minimized: true }) {
        return from(this.searchApi.organizationSearch(opts.minimized ? { minimized: opts.minimized } : undefined)).pipe(
            catchError((err) => {
                console.error(`searchApi#organizationSearch ${JSON.stringify(err, null, 2)}`);
                return of(null);
            })
        );
    }
}
